import React, { useState, useEffect } from "react";
import { Modal, Button, Checkbox, Avatar, Spin, message, Empty, Row, Col } from "antd";
import { UserOutlined, TeamOutlined, CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { assignProjectInstallers, getAssignedProjectInstallers } from "../../../services/projectServices";
import { getInstallerListing } from "../../../services/InstallerData";

const AssignInstallersModal = ({ visible, onClose, project, onUpdated }) => {
  const dispatch = useDispatch();
  const [installers, setInstallers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible && project?.id) {
      loadInstallersAndAssignments();
    }
  }, [visible, project]);

  const loadInstallersAndAssignments = async () => {
    setLoading(true);
    try {
      // 1. Fetch all installers belonging to this Trusted Dealer
      const installerListRes = await dispatch(getInstallerListing());
      const allInstallers = Array.isArray(installerListRes)
        ? installerListRes
        : installerListRes?.data || [];
      setInstallers(allInstallers);

      // 2. Fetch currently assigned installer IDs for this project
      const assignedRes = await dispatch(getAssignedProjectInstallers(project.id));
      const assigned = assignedRes?.assigned_installers || [];
      
      const assignedLookups = [];
      assigned.forEach((a) => {
        if (a.installer_user_id) assignedLookups.push(String(a.installer_user_id));
        if (a.installer?.id) assignedLookups.push(String(a.installer.id));
        if (a.installer?.wp_user_id) assignedLookups.push(String(a.installer.wp_user_id));
        if (a.installer?.email) assignedLookups.push(String(a.installer.email).toLowerCase());
      });

      // Fallback: Also include project.assigned_installer_ids if present
      if (Array.isArray(project?.assigned_installer_ids)) {
        project.assigned_installer_ids.forEach((id) => assignedLookups.push(String(id)));
      }

      // Pre-select matching installers from the installer list
      const preSelected = allInstallers
        .filter((inst) => {
          const instId = String(inst.user_id || inst.id || "");
          const instEmail = String(inst.email || inst.user_email || "").toLowerCase();
          return assignedLookups.includes(instId) || (instEmail && assignedLookups.includes(instEmail));
        })
        .map((inst) => inst.user_id || inst.id);

      setSelectedIds(preSelected);
    } catch (err) {
      console.error("Error loading installers for project assignment:", err);
      // If error or no installers, still allow existing project ids
      if (Array.isArray(project?.assigned_installer_ids)) {
        setSelectedIds(project.assigned_installer_ids);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (installerId) => {
    setSelectedIds((prev) => {
      const exists = prev.some((id) => String(id) === String(installerId));
      if (exists) {
        return prev.filter((id) => String(id) !== String(installerId));
      } else {
        return [...prev, installerId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(installers.map((inst) => inst.user_id || inst.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSave = async () => {
    if (!project?.id) return;
    setSaving(true);
    try {
      await dispatch(assignProjectInstallers(project.id, selectedIds));
      message.success("Project installers updated successfully!");
      if (onUpdated) {
        onUpdated();
      }
      onClose();
    } catch (err) {
      console.error("Error saving installer assignments:", err);
      message.error(err?.response?.data?.message || "Failed to update installer assignments");
    } finally {
      setSaving(false);
    }
  };

  const isAllSelected = installers.length > 0 && selectedIds.length === installers.length;

  return (
    <Modal
      open={visible}
      onCancel={saving ? undefined : onClose}
      footer={null}
      centered
      width={480}
      className="responsive-sub-modal"
      destroyOnClose
    >
      <div style={{ padding: "8px 4px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "#e0f2fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0284c7",
              fontSize: 20,
            }}
          >
            <TeamOutlined />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#0f172a" }}>
              Assign Installers
            </h3>
            <span style={{ fontSize: "13px", color: "#64748b" }}>
              Project: <strong style={{ color: "#0284c7" }}>{project?.name || "Untitled"}</strong>
            </span>
          </div>
        </div>

        <p style={{ color: "#475569", fontSize: "13px", marginBottom: 16, lineHeight: 1.5 }}>
          Select which installers can access this project, view photos, and upload on-site inspection images.
        </p>

        {loading ? (
          <div style={{ textAlign: "center", padding: "36px 0" }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: "#0284c7" }} spin />} />
            <div style={{ marginTop: 12, color: "#64748b", fontSize: "13px" }}>
              Loading installers...
            </div>
          </div>
        ) : installers.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: "#64748b", fontSize: "13px" }}>
                No active installers found under your account.
              </span>
            }
            style={{ padding: "20px 0" }}
          />
        ) : (
          <>
            {/* Select All Toggle */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                background: "#f1f5f9",
                borderRadius: 8,
                marginBottom: 10,
              }}
            >
              <Checkbox checked={isAllSelected} onChange={handleSelectAll}>
                <span style={{ fontWeight: 600, color: "#334155", fontSize: "13px" }}>
                  Select All ({installers.length})
                </span>
              </Checkbox>
              <span style={{ fontSize: "12px", color: "#0284c7", fontWeight: 600 }}>
                {selectedIds.length} Assigned
              </span>
            </div>

            {/* Installers List */}
            <div
              style={{
                maxHeight: "260px",
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: "4px",
              }}
            >
              {installers.map((inst) => {
                const instId = inst.user_id || inst.id;
                const isChecked = selectedIds.some((id) => String(id) === String(instId));
                const name = inst.fullname || inst.display_name || inst.name || "Installer";
                const email = inst.email || inst.user_email || "";

                return (
                  <div
                    key={instId}
                    onClick={() => handleToggle(instId)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: isChecked ? "#f0f9ff" : "#ffffff",
                      border: isChecked ? "1px solid #bae6fd" : "1px solid transparent",
                      cursor: "pointer",
                      marginBottom: 4,
                      transition: "all 0.15s ease",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Checkbox checked={isChecked} onChange={() => {}} />
                      <Avatar
                        size={32}
                        icon={<UserOutlined />}
                        style={{
                          background: isChecked ? "#0284c7" : "#cbd5e1",
                          color: "#ffffff",
                        }}
                      />
                      <div>
                        <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                          {name}
                        </div>
                        {email && (
                          <div style={{ fontSize: "12px", color: "#64748b" }}>
                            {email}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Modal Actions */}
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 20,
            paddingTop: 14,
            borderTop: "1px solid #f1f5f9",
          }}
        >
          <Button
            onClick={onClose}
            disabled={saving}
            style={{
              height: 40,
              padding: "0 18px",
              borderRadius: 8,
              fontWeight: 600,
              color: "#475569",
            }}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            icon={saving ? <LoadingOutlined /> : <CheckOutlined />}
            onClick={handleSave}
            disabled={saving || loading || installers.length === 0}
            style={{
              height: 40,
              padding: "0 22px",
              borderRadius: 8,
              fontWeight: 600,
              background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
              borderColor: "transparent",
              boxShadow: "0 2px 8px rgba(2, 132, 199, 0.3)",
            }}
          >
            {saving ? "Saving..." : "Save Assignments"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AssignInstallersModal;
